import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { DocumentSection, CommentDocThumb } from './shared/DocumentViewer';
import { normalizeDoc, compressFile, compressPdfClientSide } from '../utils/documents';
// ⚠ On importe la feuille de Litiges : les pièces jointes doivent être
// VISUELLEMENT identiques (vignettes, zone de dépôt, visionneuse). Dupliquer ces
// styles, c'est se condamner à les voir diverger.
import './Litiges.css';
import './Recouvrement.css';

// ─── Recouvrement — un dossier par CLIENT ────────────────────────────────────
// Refonte du 2026-09-20, sur le gabarit de Litiges : onglets → barre d'outils →
// chips → tableau, et une modale de détail qui porte le fil d'échanges.
//
// Ce que l'écran NE fait PAS, volontairement :
//   • il ne recalcule aucun montant — tout vient de l'entrepôt A97 DATA ;
//   • il ne recalcule aucune tranche d'ancienneté. Les libellés arrivent du
//     serveur. C'est en recopiant ces libellés côté front que le module s'est
//     retrouvé avec huit tranches sur neuf illisibles pendant cinq mois ;
//   • il n'affiche plus de pivot de balance âgée : le rapport Créances d'A97 BI
//     le fait mieux, avec l'historique et le périmètre Groupe.

const ETAPE_STYLE = {
  1: { court: 'R. commerciale', couleur: 'var(--color-info)' },
  2: { court: 'R. amiable',     couleur: 'var(--color-warning)' },
  3: { court: 'Mise en demeure', couleur: 'var(--color-error)' },
  4: { court: 'Injonction',      couleur: '#7c2d12' },
};

// Les cinq canaux d'échange. C'est ce qui manque à Litiges, dont le fil est
// mono-canal : ici on sait si le client a été appelé, écrit ou relancé.
const CANAUX = [
  { code: 'telephone',       icon: '📞', label: 'Appel' },
  { code: 'mail',            icon: '📧', label: 'E-mail' },
  { code: 'sms',             icon: '💬', label: 'SMS' },
  { code: 'courrier_simple', icon: '✉️', label: 'Courrier' },
  { code: 'courrier_rar',    icon: '📨', label: 'Courrier RAR' },
  { code: 'note',            icon: '📝', label: 'Note interne' },
];
const canalDe = (c) => CANAUX.find((x) => x.code === c) || { icon: '•', label: c };

// Contrat attendu par le DocumentSection partagé. Le PDF passe par le presign
// comme une image : le module Litiges a une route multipart pour recompresser
// ses PDF côté serveur, on ne la duplique pas ici — un justificatif de
// recouvrement n'a pas le même volume qu'un dossier photo d'atelier.
const docsApiDe = (dossierId) => ({
  presign: (id, body) => api.post(`/recouvrement/dossiers/${id}/documents/presign`, body),
  confirm: (id, body) => api.post(`/recouvrement/dossiers/${id}/documents/confirm`, body),
  rename: (id, docId, nom) => api.patch(`/recouvrement/dossiers/${id}/documents/${docId}`, { nom }),
  delete: (id, docId) => api.delete(`/recouvrement/dossiers/${id}/documents/${docId}`),
  uploadFile: async (id, formData) => {
    const file = formData.get('file');
    const { data: pre } = await api.post(`/recouvrement/dossiers/${id}/documents/presign`, {
      filename: file.name, contentType: file.type,
    });
    await fetch(pre.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    return api.post(`/recouvrement/dossiers/${id}/documents/confirm`, {
      key: pre.key, filename: file.name, contentType: file.type, size: file.size,
    });
  },
  dossierId,
});

const nf = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nf.format(Math.round(v))} €`);
const eurCourt = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return `${nf.format(Math.round(v / 1e4) / 100)} M€`;
  if (a >= 1e3) return `${nf.format(Math.round(v / 1e3))} k€`;
  return `${nf.format(Math.round(v))} €`;
};
const jour = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const auj = () => new Date().toISOString().slice(0, 10);

// ═══ La modale CLIENT — le tri ══════════════════════════════════════════════
// C'est ici qu'on décide ce qui part en recouvrement et ce qui n'en relève pas.
// Un client doit de vieilles factures en contentieux et des récentes
// parfaitement normales : les suivre ensemble rendrait l'étape ininterprétable.
function ClientModal({ cible, onClose, onChange, editMode, user }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [coches, setCoches] = useState(() => new Set());
  const [creation, setCreation] = useState(false);
  const [dossierOuvert, setDossierOuvert] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/recouvrement/clients/${cible.codsoc}/${encodeURIComponent(cible.tiers)}`);
      setD(data); setCoches(new Set());
    } catch (e) { setErreur(e.response?.data?.error || 'Chargement impossible'); }
  }, [cible]);
  useEffect(() => { charger(); }, [charger]);

  const basculer = (cle) => setCoches((s) => { const n = new Set(s); n.has(cle) ? n.delete(cle) : n.add(cle); return n; });

  const creerDossier = async () => {
    if (!coches.size || creation) return;
    setCreation(true); setErreur('');
    try {
      const { data } = await api.post('/recouvrement/dossiers', {
        codsoc: cible.codsoc, tiers: cible.tiers, cles: [...coches],
      });
      await charger(); onChange?.();
      setDossierOuvert(data.dossierId);
    } catch (e) { setErreur(e.response?.data?.error || 'Création impossible'); }
    finally { setCreation(false); }
  };

  if (!d) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body"><p className="rcv-vide">{erreur || 'Chargement…'}</p></div>
        </div>
      </div>
    );
  }

  const libres = d.lignes.filter((l) => !l.dossierId);
  const totalCoche = d.lignes.filter((l) => coches.has(l.cle)).reduce((t, l) => t + l.montant, 0);
  const c = d.contact || {};

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h2>{d.client.libelle}</h2>
              <div className="rcv-sous-titre">{d.client.tiers} · {d.client.concessionNom}</div>
            </div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          <div className="modal-body">
            {erreur && <div className="rcv-erreur">{erreur}</div>}
            {!c.adressePostaleComplete && (
              <div className="rcv-alerte">
                ⚠️ Adresse postale incomplète — aucun courrier ne pourra être envoyé à ce client
                tant qu'elle n'est pas corrigée dans Datacar.
              </div>
            )}

            <div className="rcv-entete">
              <div className="rcv-chiffre">
                <div className="rcv-chiffre-l">Total dû</div>
                <div className="rcv-chiffre-v">{eur(d.solde)}</div>
                <div className="rcv-chiffre-m">{d.lignes.length} échéance{d.lignes.length > 1 ? 's' : ''}</div>
              </div>
              <div className="rcv-coord">
                <div><span>Téléphone</span> {c.telephone || <i>non renseigné</i>}</div>
                <div><span>E-mail</span> {c.email || <i>non renseigné</i>}</div>
                <div><span>Adresse</span> {c.adresse
                  ? <>{c.adresse}{c.complement ? `, ${c.complement}` : ''} — {c.codePostal} {c.ville}</>
                  : <i>non renseignée</i>}</div>
              </div>
            </div>

            <div className="rcv-bloc">
              <h3>Dossiers de recouvrement</h3>
              {d.dossiers.length === 0 ? (
                <p className="rcv-vide">Aucun dossier ouvert. Sélectionnez des échéances ci-dessous pour en créer un.</p>
              ) : (
                <div className="rcv-dossiers">
                  {d.dossiers.map((x) => (
                    <button key={x.id} type="button" className={`rcv-dossier${x.statut === 'archive' ? ' clos' : ''}`}
                      onClick={() => setDossierOuvert(x.id)}>
                      <div className="rcv-dossier-h">
                        <b>{x.reference}</b>
                        {x.statut === 'archive'
                          ? <span className="rcv-clos">clos {x.clos_le ? jour(x.clos_le) : ''}</span>
                          : <span className="rcv-etape" style={{ borderColor: (ETAPE_STYLE[x.etape] || {}).couleur, color: (ETAPE_STYLE[x.etape] || {}).couleur }}>
                              {(ETAPE_STYLE[x.etape] || {}).court}
                            </span>}
                      </div>
                      <div className="rcv-dossier-v">{eur(x.solde)}</div>
                      <div className="rcv-dossier-m">
                        {x.nbLignes} échéance{x.nbLignes > 1 ? 's' : ''} · {x.nbEchanges} échange{x.nbEchanges > 1 ? 's' : ''}
                        {x.chargeNom ? ` · ${x.chargeNom}` : ''}
                      </div>
                      {x.prochaine_action && (
                        <div className={`rcv-dossier-d${String(x.prochaine_action).slice(0, 10) < auj() ? ' rcv-retard' : ''}`}>
                          prochaine action {jour(x.prochaine_action)}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="rcv-bloc">
              <div className="rcv-bloc-h">
                <h3>Échéances impayées</h3>
                {coches.size > 0 && (
                  <button type="button" className="rcv-btn" disabled={creation} onClick={creerDossier}>
                    {creation ? 'Création…' : `Créer un dossier — ${coches.size} échéance${coches.size > 1 ? 's' : ''} · ${eur(totalCoche)}`}
                  </button>
                )}
              </div>
              {libres.length === 0 && <p className="rcv-vide">Toutes les échéances sont rattachées à un dossier.</p>}
              <div className="rcv-scroll">
                <table className="rcv-tbl">
                  <thead><tr>
                    <th className="rcv-coche"> </th><th>Échéance</th><th>Référence</th><th>Ancienneté</th>
                    <th>Libellé</th><th className="num">Montant</th><th>Dossier</th>
                  </tr></thead>
                  <tbody>
                    {d.lignes.map((l) => (
                      <tr key={l.cle} className={l.dossierId ? 'rcv-prise' : ''}>
                        <td className="rcv-coche">
                          {/* Une échéance déjà rattachée n'est plus sélectionnable :
                              elle n'appartient qu'à UN dossier, sans quoi deux
                              personnes relanceraient la même facture. */}
                          <input type="checkbox" disabled={!!l.dossierId}
                            checked={coches.has(l.cle)} onChange={() => basculer(l.cle)} />
                        </td>
                        <td>{jour(l.echeance)}</td>
                        <td className="rcv-ref">{l.reference || '—'}</td>
                        <td>{l.tranche}</td>
                        <td className="rcv-libelle"><span>{l.libelle || '—'}</span></td>
                        <td className={`num${l.montant < 0 ? ' rcv-credit' : ''}`}>{eur(l.montant)}</td>
                        <td>{l.dossierRef || <span className="rcv-neuf">libre</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={5}>Total dû — dont {eur(d.soldeNonAffecte)} hors dossier</td>
                    <td className="num">{eur(d.solde)}</td><td />
                  </tr></tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {dossierOuvert && (
        <DossierModal dossierId={dossierOuvert} editMode={editMode} user={user}
          onClose={() => { setDossierOuvert(null); charger(); onChange?.(); }}
          onChange={() => { charger(); onChange?.(); }} />
      )}
    </>
  );
}

// ═══ La modale DOSSIER — le suivi ═══════════════════════════════════════════
function DossierModal({ dossierId, onClose, onChange, editMode, user }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [canal, setCanal] = useState('telephone');
  const [notes, setNotes] = useState('');
  const [suite, setSuite] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [lettre, setLettre] = useState(false);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [piece, setPiece] = useState(null);   // pièce jointe au message en cours
  const fichierRef = useRef(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/recouvrement/dossiers/${dossierId}`);
      setD(data); setDocuments((data.documents || []).map(normalizeDoc));
    } catch (e) { setErreur(e.response?.data?.error || 'Chargement impossible'); }
  }, [dossierId]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { api.get('/users').then((r) => setUsers(r.data.users || r.data || [])).catch(() => {}); }, []);

  const patch = async (champ, valeur) => {
    try {
      const { data } = await api.patch(`/recouvrement/dossiers/${dossierId}`, { [champ]: valeur });
      setD((x) => ({ ...x, dossier: { ...x.dossier, ...data.dossier } }));
      onChange?.();
    } catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); }
  };

  // Une pièce se joint AU MESSAGE : on crée l'échange, puis on téléverse en lui
  // passant son id — exactement la séquence de Litiges.
  const choisirPiece = async (file) => {
    if (!file) return;
    const prepare = file.type === 'application/pdf' ? await compressPdfClientSide(file)
      : file.type.startsWith('image/') ? await compressFile(file) : file;
    setPiece({ file: prepare, name: prepare.name, type: prepare.type, url: URL.createObjectURL(prepare) });
  };
  const retirerPiece = () => { if (piece?.url) URL.revokeObjectURL(piece.url); setPiece(null); };

  const ajouterEchange = async () => {
    if (!notes.trim() || envoi) return;
    setEnvoi(true);
    try {
      const { data } = await api.post(`/recouvrement/dossiers/${dossierId}/actions`, {
        type_action: canal, notes: notes.trim(), prochaine_action: suite || null,
      });
      if (piece) {
        const { data: pre } = await api.post(`/recouvrement/dossiers/${dossierId}/documents/presign`, {
          filename: piece.name, contentType: piece.type,
        });
        await fetch(pre.uploadUrl, { method: 'PUT', body: piece.file, headers: { 'Content-Type': piece.type } });
        await api.post(`/recouvrement/dossiers/${dossierId}/documents/confirm`, {
          key: pre.key, filename: piece.name, contentType: piece.type, size: piece.file.size,
          actionId: data.action.id,
        });
        retirerPiece();
      }
      setNotes(''); setSuite('');
      await charger(); onChange?.();
    } catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); }
    finally { setEnvoi(false); }
  };

  const genererLettre = async () => {
    if (lettre) return;
    setLettre(true); setErreur('');
    try {
      const { data } = await api.post(`/recouvrement/dossiers/${dossierId}/lettre`);
      if (data.document?.url) window.open(data.document.url, '_blank', 'noopener');
      await charger(); onChange?.();
    } catch (e) { setErreur(e.response?.data?.error || "La lettre n'a pas pu être générée"); }
    finally { setLettre(false); }
  };

  if (!d) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body"><p className="rcv-vide">{erreur || 'Chargement…'}</p></div>
        </div>
      </div>
    );
  }

  const dos = d.dossier;
  const c = d.contact || {};
  const enRetard = dos.prochaine_action && String(dos.prochaine_action).slice(0, 10) < auj();
  const clos = dos.statut === 'archive';
  const aEncaisse = d.recouvre > 0;

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{dos.reference} — {dos.tiers_libelle}</h2>
            <div className="rcv-sous-titre">
              ouvert le {jour(dos.ouvert_le)}{clos ? ` · clos le ${jour(dos.clos_le)}` : ''} · {dos.concessionNom}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {erreur && <div className="rcv-erreur">{erreur}</div>}
          {/* Mode édition + super-admin : un dossier ouvert par erreur doit
              pouvoir disparaître. L'archiver le laisserait dans l'historique du
              client comme s'il avait existé. */}
          {editMode && user?.role === 'super-admin' && (
            <div className="rcv-danger">
              <span>Dossier créé par erreur ?</span>
              <button type="button" className="btn-icon delete" title="Supprimer définitivement ce dossier"
                onClick={async () => {
                  if (!window.confirm(`Supprimer définitivement le dossier ${dos.reference} ?\n\nSes échanges et ses pièces seront perdus. Les échéances redeviendront libres.`)) return;
                  try { await api.delete(`/recouvrement/dossiers/${dossierId}`); onChange?.(); onClose(); }
                  catch (e) { setErreur(e.response?.data?.error || 'Suppression impossible'); }
                }}>🗑️</button>
            </div>
          )}
          {!c.adressePostaleComplete && (
            <div className="rcv-alerte">
              ⚠️ Adresse postale incomplète — la lettre ne pourra pas être adressée.
            </div>
          )}

          <div className="rcv-entete">
            <div className="rcv-chiffre">
              <div className="rcv-chiffre-l">Reste dû</div>
              <div className="rcv-chiffre-v">{eur(d.solde)}</div>
              <div className="rcv-chiffre-m">
                {/* Ce qui a été encaissé depuis l'ouverture : la seule mesure de
                    recouvrement dont on dispose, l'entrepôt ne gardant aucun
                    historique. */}
                {d.recouvre > 0 ? `${eur(d.recouvre)} recouvrés sur ${eur(d.montantInitial)}` : `${eur(d.montantInitial)} à l'ouverture`}
              </div>
            </div>
            <div className="rcv-reglages">
              <label>
                <span>Étape</span>
                <select value={dos.etape} disabled={clos} onChange={(e) => patch('etape', Number(e.target.value))}>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{d.etapes[n]}</option>)}
                </select>
              </label>
              <label>
                <span>Chargé</span>
                <select value={dos.charge_id || ''} onChange={(e) => patch('charge_id', e.target.value ? Number(e.target.value) : null)}>
                  <option value="">—</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                </select>
              </label>
              <label>
                <span>Prochaine action</span>
                <input type="date" className={enRetard ? 'rcv-retard' : ''}
                  value={dos.prochaine_action ? String(dos.prochaine_action).slice(0, 10) : ''}
                  onChange={(e) => patch('prochaine_action', e.target.value || null)} />
              </label>
              <label>
                <span>{clos ? 'Motif de clôture' : 'Statut'}</span>
                {clos ? (
                  <select value={dos.motif_cloture || ''} onChange={(e) => patch('motif_cloture', e.target.value || null)}>
                    <option value="">—</option>
                    <option value="paye">Payé</option>
                    <option value="abandonne">Abandonné</option>
                    <option value="contentieux">Contentieux</option>
                    <option value="autre">Autre</option>
                  </select>
                ) : (
                  <select value={dos.statut} onChange={(e) => patch('statut', e.target.value)}>
                    <option value="actif">Actif</option>
                    <option value="suspendu">Suspendu</option>
                    <option value="archive">Clore le dossier</option>
                  </select>
                )}
              </label>
            </div>
          </div>

          <div className="rcv-bloc">
            <h3>Échéances de ce dossier</h3>
            <div className="rcv-scroll">
              <table className="rcv-tbl">
                <thead><tr>
                  <th>Échéance</th><th>Référence</th><th>Ancienneté</th><th>Libellé</th>
                  {aEncaisse && <th className="num">À l'ouverture</th>}
                  <th className="num">Reste dû</th><th />
                </tr></thead>
                <tbody>
                  {d.lignes.map((l) => (
                    <tr key={l.id} className={l.statut === 'solde' ? 'rcv-soldee' : ''}>
                      <td>{jour(l.echeance)}</td>
                      <td className="rcv-ref">{l.reference || '—'}</td>
                      <td>{l.tranche || '—'}</td>
                      <td className="rcv-libelle"><span>{l.libelle || '—'}</span></td>
                      {aEncaisse && <td className="num">{eur(l.montantInitial)}</td>}
                      <td className="num">{l.statut === 'solde' ? <b className="rcv-ok">réglée</b> : eur(l.montant)}</td>
                      <td>
                        {!clos && l.statut !== 'solde' && (
                          <button type="button" className="rcv-detacher" title="Retirer du dossier"
                            onClick={async () => {
                              try { await api.delete(`/recouvrement/dossiers/${dossierId}/lignes/${l.id}`); await charger(); onChange?.(); }
                              catch (e) { setErreur(e.response?.data?.error || 'Retrait impossible'); }
                            }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td colSpan={4}>Total</td>
                  {aEncaisse && <td className="num">{eur(d.montantInitial)}</td>}
                  <td className="num">{eur(d.solde)}</td><td />
                </tr></tfoot>
              </table>
            </div>
          </div>

          <div className="rcv-bloc">
            <div className="rcv-bloc-h">
              <h3>Courriers et pièces</h3>
              <button type="button" className="rcv-btn" disabled={lettre || clos || !c.adressePostaleComplete}
                title={c.adressePostaleComplete ? `Lettre de niveau ${dos.etape} — ${dos.etapeLibelle}`
                  : 'Adresse postale incomplète : la lettre ne peut pas être adressée'}
                onClick={genererLettre}>
                {lettre ? 'Génération…' : `📄 Générer la lettre — ${dos.etapeLibelle}`}
              </button>
            </div>
            {/* Le composant PARTAGÉ avec Litiges : même zone de dépôt, mêmes
                vignettes, même visionneuse. */}
            <DocumentSection
              documents={documents.filter((x) => !x.action_id)}
              setDocuments={(maj) => {
                const suite = typeof maj === 'function' ? maj(documents.filter((x) => !x.action_id)) : maj;
                setDocuments([...documents.filter((x) => x.action_id), ...suite]);
              }}
              parentId={dossierId} docsApi={docsApiDe(dossierId)} canEdit={!clos} />
          </div>

          <div className="rcv-bloc">
            <h3>Échanges avec le client</h3>
            {!clos && (
              <div className="rcv-saisie">
                <div className="rcv-canaux">
                  {CANAUX.map((x) => (
                    <button key={x.code} type="button" className={`rcv-canal${canal === x.code ? ' on' : ''}`}
                      onClick={() => setCanal(x.code)}>{x.icon} {x.label}</button>
                  ))}
                </div>
                <div className="rcv-saisie-corps">
                  <textarea rows={3} placeholder="Ce qui a été dit, ce qui a été convenu…"
                    value={notes} onChange={(e) => setNotes(e.target.value)} />
                  {/* Joindre une pièce AU MESSAGE : zone de dépôt identique à
                      celle de Litiges, à côté du texte. */}
                  <div className="rcv-piece" onClick={() => fichierRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); choisirPiece(e.dataTransfer.files?.[0]); }}>
                    {piece ? (
                      <>
                        <CommentDocThumb doc={{ ...piece, nom: piece.name }} />
                        <button type="button" className="rcv-piece-x"
                          onClick={(e) => { e.stopPropagation(); retirerPiece(); }}>✕</button>
                      </>
                    ) : <span>📎 Glisser<br />un justificatif</span>}
                    <input ref={fichierRef} type="file" accept="image/*,application/pdf" hidden
                      onChange={(e) => { choisirPiece(e.target.files?.[0]); e.target.value = ''; }} />
                  </div>
                </div>
                <div className="rcv-saisie-bas">
                  <label>Prochaine action <input type="date" value={suite} onChange={(e) => setSuite(e.target.value)} /></label>
                  <button type="button" className="rcv-btn" disabled={!notes.trim() || envoi} onClick={ajouterEchange}>
                    {envoi ? 'Enregistrement…' : 'Enregistrer l’échange'}
                  </button>
                </div>
              </div>
            )}

            {d.actions.length === 0 ? (
              <p className="rcv-vide">Aucun échange enregistré pour l’instant.</p>
            ) : (
              <ul className="rcv-fil">
                {d.actions.map((a) => {
                  const k = canalDe(a.type_action);
                  const pieces = documents.filter((x) => x.action_id === a.id);
                  return (
                    <li key={a.id}>
                      <div className="rcv-fil-h">
                        <span className="rcv-fil-canal">{k.icon} {k.label}</span>
                        <b>{[a.user_prenom, a.user_nom].filter(Boolean).join(' ') || '—'}</b>
                        <span className="rcv-fil-date">{new Date(a.date_action).toLocaleString('fr-FR')}</span>
                        {a.montant_du != null && (
                          <span className="rcv-fil-montant" title="Montant dû par ce dossier au moment de cet échange">
                            {eur(Number(a.montant_du))}
                          </span>
                        )}
                      </div>
                      <div className="rcv-fil-texte">{a.notes}</div>
                      {pieces.length > 0 && (
                        <div className="rcv-fil-pieces">
                          {pieces.map((x) => <CommentDocThumb key={x.id} doc={x} />)}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══ L'écran ═════════════════════════════════════════════════════════════════
export default function Recouvrement({ user, setHeaderAction, editMode }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState('dossiers');   // dossiers | archives
  const [tranche, setTranche] = useState(null);
  const [aTraiter, setATraiter] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [concession, setConcession] = useState('');
  const [page, setPage] = useState(0);
  const [ouvert, setOuvert] = useState(null);   // { codsoc, tiers }
  const [stats, setStats] = useState(null);

  useEffect(() => { setHeaderAction?.(null); return () => setHeaderAction?.(null); }, [setHeaderAction]);

  const charger = useCallback(async () => {
    setErreur('');
    const p = new URLSearchParams();
    if (onglet === 'archives') p.set('archives', '1');
    if (tranche != null) p.set('tranche', String(tranche));
    if (aTraiter) p.set('a_traiter', '1');
    if (recherche.trim()) p.set('search', recherche.trim());
    if (concession) p.set('concession', concession);
    p.set('page', String(page));
    try {
      const { data: d } = await api.get(`/recouvrement/dossiers?${p}`);
      setData(d);
    } catch (e) { setErreur(e.response?.data?.error || 'Chargement impossible'); setData(null); }
  }, [onglet, tranche, aTraiter, recherche, concession, page]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { api.get('/recouvrement/stats').then((r) => setStats(r.data)).catch(() => {}); }, [onglet]);

  // Une recherche, un chip ou un onglet ramènent en première page : rester
  // page 7 d'une liste qui n'en fait plus que 2 donne un tableau vide.
  useEffect(() => { setPage(0); }, [onglet, tranche, aTraiter, recherche, concession]);

  // On ouvre le CLIENT : c'est là qu'on voit ses dossiers et ce qui n'est
  // rattaché à aucun. Un dossier ne se crée jamais tout seul.
  const ouvrir = (l) => setOuvert({ codsoc: l.codsoc, tiers: l.tiers });

  const nbPages = useMemo(() => (data ? Math.max(1, Math.ceil(data.nb / data.parPage)) : 1), [data]);

  if (erreur && !data) {
    return <div className="page-container"><div className="rcv-erreur">{erreur}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={onglet === 'dossiers' ? 'active' : ''} onClick={() => setOnglet('dossiers')}>
          💳 Dossiers{data && onglet === 'dossiers' ? <span className="tab-count">{nf.format(data.nb)}</span> : null}
        </button>
        <button className={onglet === 'archives' ? 'active' : ''} onClick={() => setOnglet('archives')}>
          🗄️ Archivés{data && onglet === 'archives' ? <span className="tab-count">{nf.format(data.nb)}</span> : null}
        </button>
      </div>

      {stats && !stats.indisponible && (
        <div className="rcv-kpis">
          <div className="rcv-kpi"><div className="l">Encours</div><div className="v">{eurCourt(stats.encours)}</div>
            <div className="m">{nf.format(stats.clients)} clients</div></div>
          <div className="rcv-kpi"><div className="l">Dont + 90 jours</div><div className="v">{eurCourt(stats.retard90)}</div>
            <div className="m">{stats.encours ? Math.round((stats.retard90 / stats.encours) * 100) : 0} % de l'encours</div></div>
          <div className="rcv-kpi"><div className="l">Dossiers ouverts</div><div className="v">{nf.format(stats.dossiersOuverts)}</div>
            <div className="m">{nf.format(stats.dossiersArchives)} archivés</div></div>
          <div className="rcv-kpi"><div className="l">À traiter</div><div className="v">{nf.format(stats.aTraiter)}</div>
            <div className="m">sans date ou échue</div></div>
        </div>
      )}

      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Code client ou raison sociale…"
          value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        {(data?.concessionsDispo || []).length > 1 && (
          <select className={`page-select${concession ? ' active-filter' : ''}`} value={concession} onChange={(e) => setConcession(e.target.value)}
            title="Restreindre à une concession">
            <option value="">Toutes les concessions</option>
            {data.concessionsDispo.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}
        <button className={`rcv-toggle${aTraiter ? ' on' : ''}`} onClick={() => setATraiter((v) => !v)}
          title="Dossiers sans prochaine action, ou dont la date est passée">☑️ À traiter</button>
      </div>

      {/* ⚠ Les classes d'état sont `chip-default` / `chip-active` / `chip-empty`
          (Dashboard.css:818-860), PAS `active`. Avec une classe inventée, le chip
          n'a ni fond ni couleur de bordure — `.filter-chip` déclare
          `border: 1px solid` SANS couleur — et paraît donc inerte : le clic
          partait bien, il ne se voyait pas. `chip-empty` porte en revanche un
          vrai `pointer-events: none` : une tranche vide n'est pas cliquable, et
          c'est voulu.
          ⚠ Un commentaire JSX ne peut pas vivre juste après `{data && (` : c'est
          une position d'EXPRESSION, pas du JSX. Il doit rester dehors. */}
      {data && (
        <div className="filter-chips-bar">
          <span className="filter-chips-label">Ancienneté</span>
          <button type="button" className={`filter-chip ${tranche === null ? 'chip-active' : 'chip-default'}`}
            onClick={() => setTranche(null)}>
            Toutes<span className="chip-count">{nf.format(data.parTranche.reduce((a, b) => a + b, 0))}</span>
          </button>
          {data.tranches.map((t, i) => {
            const n = data.parTranche[i];
            const cls = n === 0 ? 'chip-empty' : tranche === i ? 'chip-active' : 'chip-default';
            return (
              <button key={i} type="button" className={`filter-chip ${cls}`} disabled={n === 0}
                onClick={() => setTranche(tranche === i ? null : i)}>
                {t}<span className="chip-count">{nf.format(n)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="table-container">
        <table className="rcv-tbl rcv-liste">
          <thead>
            <tr>
              <th>Client</th><th className="num">Solde dû</th>
              <th className="num">Échéances</th><th>Plus ancienne</th><th>Ancienneté</th>
              <th>Étape</th><th>Chargé</th><th>Prochaine action</th><th>Dernier échange</th>
            </tr>
          </thead>
          <tbody>
            {(data?.dossiers || []).map((l) => {
              const st = ETAPE_STYLE[l.etape];
              const retard = l.prochaineAction && String(l.prochaineAction).slice(0, 10) < auj();
              return (
                <tr key={`${l.codsoc}|${l.tiers}`} onClick={() => ouvrir(l)} className="rcv-clic">
                  <td><b>{l.libelle}</b><span className="rcv-code">{l.tiers}</span></td>
                  <td className={`num rcv-solde${l.solde < 0 ? ' rcv-credit' : ''}`}>{eur(l.solde)}</td>
                  <td className="num">{l.nbEcheances}</td>
                  <td>{jour(l.plusAncienne)}</td>
                  <td>{data.tranches[l.tranche]}</td>
                  <td>
                    {st ? (
                      <span className="rcv-etape" style={{ borderColor: st.couleur, color: st.couleur }}>
                        {st.court}{l.nbDossiers > 1 ? ` +${l.nbDossiers - 1}` : ''}
                      </span>
                    ) : <span className="rcv-neuf">aucun dossier</span>}
                  </td>
                  <td>{l.chargeNom || '—'}</td>
                  <td className={retard ? 'rcv-retard' : ''}>{jour(l.prochaineAction)}</td>
                  <td>{jour(l.derniereAction)}</td>
                </tr>
              );
            })}
            {data && data.dossiers.length === 0 && (
              <tr><td colSpan={9} className="rcv-vide">Aucun dossier ne correspond.</td></tr>
            )}
          </tbody>
          {data && data.nb > 0 && (
            <tfoot>
              {/* Le pied totalise les lignes RETENUES, jamais la page affichée :
                  un total qui changerait en tournant les pages ne dirait rien. */}
              <tr><td>Total — {nf.format(data.nb)} client{data.nb > 1 ? 's' : ''}</td>
                <td className="num">{eur(data.total)}</td><td colSpan={7} /></tr>
            </tfoot>
          )}
        </table>
      </div>

      {data && nbPages > 1 && (
        <div className="rcv-pages">
          <button className="rcv-btn ghost" disabled={page === 0} onClick={() => setPage(0)}>«</button>
          <button className="rcv-btn ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Précédents</button>
          <span className="rcv-pages-n">
            {nf.format(page * data.parPage + 1)}–{nf.format(Math.min((page + 1) * data.parPage, data.nb))} sur {nf.format(data.nb)}
            <small>page {page + 1} / {nbPages}</small>
          </span>
          <button className="rcv-btn ghost" disabled={page >= nbPages - 1} onClick={() => setPage(page + 1)}>Suivants ›</button>
          <button className="rcv-btn ghost" disabled={page >= nbPages - 1} onClick={() => setPage(nbPages - 1)}>»</button>
        </div>
      )}

      {ouvert && (
        <ClientModal cible={ouvert} editMode={editMode} user={user}
          onClose={() => { setOuvert(null); charger(); }}
          onChange={charger} />
      )}
    </div>
  );
}
